import { RefreshCw, Database, Network, Server, Zap } from 'lucide-react';
import { cn } from '@/lib/cn';
import { opsApi } from '@/lib/ops-api';
import { usePolling } from '@/hooks/usePolling';
import StatusBadge from '@/components/StatusBadge';
import type { PlatformHealthResponse } from '@/lib/types';

export default function PlatformHealthPage() {
  const { data, error, loading, refresh } = usePolling<PlatformHealthResponse>(
    () => opsApi.getPlatformHealth(),
    30_000,
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Platform Health</h1>
          <p className="text-surface-500 text-sm mt-1">
            Core infrastructure status
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <StatusBadge
              status={data.overall_status === 'healthy' ? 'healthy' : 'critical'}
              label={data.overall_status === 'healthy' ? 'All Systems Operational' : 'Degraded'}
            />
          )}
          <button
            onClick={refresh}
            disabled={loading}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-surface-400',
              'bg-white/[0.03] border border-white/[0.06]',
              'hover:text-surface-200 hover:bg-white/[0.05] transition-all',
              'disabled:opacity-50',
            )}
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading && !data ? (
        <HealthSkeleton />
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* ECS Cluster */}
          <InfraCard
            icon={<Server className="w-5 h-5" />}
            title="ECS Cluster"
            status={data.ecs_cluster.status}
          >
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Active Services" value={data.ecs_cluster.active_services} />
              <MiniStat label="Running Tasks" value={data.ecs_cluster.running_tasks} />
              <MiniStat label="Pending Tasks" value={data.ecs_cluster.pending_tasks} warn={data.ecs_cluster.pending_tasks > 0} />
              <MiniStat label="Total Services" value={data.ecs_cluster.total_services} />
            </div>
          </InfraCard>

          {/* RDS */}
          <InfraCard
            icon={<Database className="w-5 h-5" />}
            title="RDS PostgreSQL"
            status={data.rds.status}
          >
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Instance Class" value={data.rds.instance_class} />
              <MiniStat label="Engine" value={`${data.rds.engine} ${data.rds.engine_version}`} />
              <MiniStat label="Storage" value={`${data.rds.storage_gb} GB`} />
              <MiniStat label="Multi-AZ" value={data.rds.multi_az ? 'Yes' : 'No'} />
            </div>
          </InfraCard>

          {/* ALB */}
          <InfraCard
            icon={<Network className="w-5 h-5" />}
            title="Application Load Balancer"
            status={data.alb.status}
          >
            {data.alb.target_groups.length > 0 ? (
              <div className="space-y-2">
                {data.alb.target_groups.map((tg) => (
                  <div key={tg.name} className="flex items-center justify-between text-sm">
                    <span className="text-surface-400 truncate font-mono text-xs">{tg.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-status-healthy font-mono">{tg.healthy_targets}</span>
                      <span className="text-surface-700">/</span>
                      <span className="text-surface-400 font-mono">{tg.total_targets}</span>
                      {tg.unhealthy_targets > 0 && (
                        <span className="text-status-critical text-xs">
                          ({tg.unhealthy_targets} unhealthy)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-surface-600 text-sm">No target groups</p>
            )}
          </InfraCard>

          {/* Lambda Errors */}
          <InfraCard
            icon={<Zap className="w-5 h-5" />}
            title="Lambda Errors (1h)"
            status={Object.values(data.lambda_errors_1h).every(v => v === 0) ? 'ACTIVE' : 'DEGRADED'}
          >
            <div className="space-y-2">
              {Object.entries(data.lambda_errors_1h).map(([component, count]) => (
                <div key={component} className="flex items-center justify-between text-sm">
                  <span className="text-surface-400 capitalize">{component}</span>
                  <span className={cn(
                    'font-mono font-medium',
                    count > 0 ? 'text-status-critical' : 'text-status-healthy',
                  )}>
                    {count}
                  </span>
                </div>
              ))}
              {Object.keys(data.lambda_errors_1h).length === 0 && (
                <p className="text-surface-600 text-sm">No error data available</p>
              )}
            </div>
          </InfraCard>
        </div>
      ) : null}

      {data?.checked_at && (
        <p className="text-xs text-surface-600 text-center">
          Last checked: {new Date(data.checked_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function InfraCard({
  icon,
  title,
  status,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  status: string;
  children: React.ReactNode;
}) {
  const isHealthy = ['ACTIVE', 'available', 'active'].includes(status);

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.04]">
        <div className="flex items-center gap-3">
          <span className="text-primary-400">{icon}</span>
          <h3 className="text-white font-medium text-sm">{title}</h3>
        </div>
        <span className={cn(
          'text-xs font-mono px-2 py-0.5 rounded',
          isHealthy
            ? 'bg-status-healthy/15 text-status-healthy'
            : 'bg-status-critical/15 text-status-critical',
        )}>
          {status}
        </span>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  );
}

function MiniStat({ label, value, warn = false }: { label: string; value: string | number; warn?: boolean }) {
  return (
    <div>
      <p className="text-xs text-surface-600 mb-0.5">{label}</p>
      <p className={cn(
        'text-sm font-mono font-medium',
        warn ? 'text-status-warning' : 'text-white',
      )}>
        {value}
      </p>
    </div>
  );
}

function HealthSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
          <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/[0.04]">
            <div className="w-5 h-5 bg-surface-700 rounded" />
            <div className="h-4 w-32 bg-surface-700 rounded" />
          </div>
          <div className="p-5 space-y-3">
            <div className="h-4 w-full bg-surface-800 rounded" />
            <div className="h-4 w-3/4 bg-surface-800 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
